import React from 'react';
import { getEdgeAppearance } from '../../../support/journeyMap/normalizers';
import { JOURNEY_NODE_HEIGHT, JOURNEY_NODE_WIDTH } from '../../../support/journeyMap/constants';

type Props = {
  resolvedLinks: Array<{
    raw: any;
    sourceId: string;
    targetId: string;
    type: string;
  }>;
  itemById: Record<string, any>;
  bounds: {
    width: number;
    height: number;
  };
};

type Side = 'left' | 'right' | 'top' | 'bottom';

type AnchoredPoints = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startSide: Side;
  endSide: Side;
};

const ANCHOR_OUTSET = 10;
const MIN_STUB = 20;

const getLinkLabel = (link: any, type: string) => {
  const explicit = String(link?.raw?.label ?? link?.label ?? '').trim();
  if (explicit) return explicit;

  switch (String(type || 'default').toLowerCase()) {
    case 'alternate':
      return 'Alternate';
    case 'conditional':
      return 'Conditional';
    case 'test_out':
      return 'Test Out';
    case 'optional':
      return 'Optional';
    case 'fallback':
      return 'Fallback';
    case 'pass':
      return 'Pass';
    case 'fail':
      return 'Fail';
    case 'skip':
      return 'Skip';
    default:
      return '';
  }
};

const getLinkShape = (link: any): 'straight' | 'step' | 'smoothstep' | 'bezier' => {
  const rawShape = String(
    link?.raw?.condition_config?.edge_shape || link?.raw?.edge_shape || '',
  )
    .trim()
    .toLowerCase();

  if (rawShape === 'straight') return 'straight';
  if (rawShape === 'step') return 'step';
  if (rawShape === 'smoothstep') return 'smoothstep';
  if (rawShape === 'bezier') return 'bezier';

  // Admin default is squared, so viewer default must not be curved.
  return 'step';
};

const getAnchoredPoints = (source: any, target: any): AnchoredPoints => {
  const sourceLeft = Number(source?._x || 0);
  const sourceTop = Number(source?._y || 0);
  const targetLeft = Number(target?._x || 0);
  const targetTop = Number(target?._y || 0);

  const sourceCenterX = sourceLeft + JOURNEY_NODE_WIDTH / 2;
  const sourceCenterY = sourceTop + JOURNEY_NODE_HEIGHT / 2;
  const targetCenterX = targetLeft + JOURNEY_NODE_WIDTH / 2;
  const targetCenterY = targetTop + JOURNEY_NODE_HEIGHT / 2;

  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;

  // Dominant direction chooses exit/entry sides.
  if (Math.abs(dx) >= Math.abs(dy)) {
    if (dx >= 0) {
      return {
        startX: sourceLeft + JOURNEY_NODE_WIDTH + ANCHOR_OUTSET,
        startY: sourceCenterY,
        endX: targetLeft - ANCHOR_OUTSET,
        endY: targetCenterY,
        startSide: 'right',
        endSide: 'left',
      };
    }

    return {
      startX: sourceLeft - ANCHOR_OUTSET,
      startY: sourceCenterY,
      endX: targetLeft + JOURNEY_NODE_WIDTH + ANCHOR_OUTSET,
      endY: targetCenterY,
      startSide: 'left',
      endSide: 'right',
    };
  }

  if (dy >= 0) {
    return {
      startX: sourceCenterX,
      startY: sourceTop + JOURNEY_NODE_HEIGHT + ANCHOR_OUTSET,
      endX: targetCenterX,
      endY: targetTop - ANCHOR_OUTSET,
      startSide: 'bottom',
      endSide: 'top',
    };
  }

  return {
    startX: sourceCenterX,
    startY: sourceTop - ANCHOR_OUTSET,
    endX: targetCenterX,
    endY: targetTop + JOURNEY_NODE_HEIGHT + ANCHOR_OUTSET,
    startSide: 'top',
    endSide: 'bottom',
  };
};

const getStraightPath = ({
                           startX,
                           startY,
                           endX,
                           endY,
                         }: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) => `M ${startX} ${startY} L ${endX} ${endY}`;

const getStepPath = ({
                       startX,
                       startY,
                       endX,
                       endY,
                       startSide,
                       endSide,
                     }: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startSide: Side;
  endSide: Side;
}) => {
  const startIsHorizontal = startSide === 'left' || startSide === 'right';
  const endIsHorizontal = endSide === 'left' || endSide === 'right';

  // Build short "stub" from source and into target so edge visually exits/enters from card.
  const startStubX =
    startSide === 'right'
      ? startX + MIN_STUB
      : startSide === 'left'
        ? startX - MIN_STUB
        : startX;
  const startStubY =
    startSide === 'bottom'
      ? startY + MIN_STUB
      : startSide === 'top'
        ? startY - MIN_STUB
        : startY;

  const endStubX =
    endSide === 'left'
      ? endX - MIN_STUB
      : endSide === 'right'
        ? endX + MIN_STUB
        : endX;
  const endStubY =
    endSide === 'top'
      ? endY - MIN_STUB
      : endSide === 'bottom'
        ? endY + MIN_STUB
        : endY;

  // Route orthogonally between stubs.
  if (startIsHorizontal && endIsHorizontal) {
    const midX = (startStubX + endStubX) / 2;
    return `M ${startX} ${startY} L ${startStubX} ${startStubY} L ${midX} ${startStubY} L ${midX} ${endStubY} L ${endStubX} ${endStubY} L ${endX} ${endY}`;
  }

  if (!startIsHorizontal && !endIsHorizontal) {
    const midY = (startStubY + endStubY) / 2;
    return `M ${startX} ${startY} L ${startStubX} ${startStubY} L ${startStubX} ${midY} L ${endStubX} ${midY} L ${endStubX} ${endStubY} L ${endX} ${endY}`;
  }

  // Mixed orientation
  if (startIsHorizontal) {
    return `M ${startX} ${startY} L ${startStubX} ${startStubY} L ${endStubX} ${startStubY} L ${endStubX} ${endStubY} L ${endX} ${endY}`;
  }

  return `M ${startX} ${startY} L ${startStubX} ${startStubY} L ${startStubX} ${endStubY} L ${endStubX} ${endStubY} L ${endX} ${endY}`;
};

const getSmoothStepPath = ({
                             startX,
                             startY,
                             endX,
                             endY,
                             startSide,
                             endSide,
                           }: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startSide: Side;
  endSide: Side;
}) => {
  // Smooth version built from step waypoints.
  const stepPath = getStepPath({ startX, startY, endX, endY, startSide, endSide });
  // Keep compatibility/simple output; step path works reliably for now.
  // If desired later, replace with rounded-corner polyline algorithm.
  return stepPath;
};

const getShapeAwarePath = ({
                             shape,
                             startX,
                             startY,
                             endX,
                             endY,
                             startSide,
                             endSide,
                           }: {
  shape: 'straight' | 'step' | 'smoothstep' | 'bezier';
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startSide: Side;
  endSide: Side;
}) => {
  if (shape === 'straight') {
    return getStraightPath({ startX, startY, endX, endY });
  }

  if (shape === 'step') {
    return getStepPath({ startX, startY, endX, endY, startSide, endSide });
  }

  if (shape === 'smoothstep') {
    return getSmoothStepPath({ startX, startY, endX, endY, startSide, endSide });
  }

  const deltaX = endX - startX;
  const curveOffset = Math.max(70, Math.abs(deltaX) * 0.35);
  const control1X = startX + (startSide === 'left' ? -curveOffset : curveOffset);
  const control2X = endX + (endSide === 'left' ? -curveOffset : curveOffset);

  return `M ${startX} ${startY} C ${control1X} ${startY}, ${control2X} ${endY}, ${endX} ${endY}`;
};

const getLabelDimensions = (label: string) => {
  const width = Math.max(56, label.length * 7 + 16);
  const height = 24;
  return { width, height };
};

const getLabelCenter = ({
                          startX,
                          startY,
                          endX,
                          endY,
                        }: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) => {
  return {
    x: (startX + endX) / 2,
    y: (startY + endY) / 2,
  };
};

const JourneyMapEdgeLayer: React.FC<Props> = ({ resolvedLinks, itemById, bounds }) => {
  return (
    <svg
      className="journey-map-board__edges"
      width={bounds.width}
      height={bounds.height}
      viewBox={`0 0 ${bounds.width} ${bounds.height}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <defs>
        <marker
          id="journey-map-arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="8"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="var(--hbr-color-neutral-border)" />
        </marker>
      </defs>

      {resolvedLinks.map((link, idx) => {
        const source = itemById[link.sourceId];
        const target = itemById[link.targetId];
        if (!source || !target) return null;

        const { startX, startY, endX, endY, startSide, endSide } = getAnchoredPoints(source, target);
        const shape = getLinkShape(link);

        const path = getShapeAwarePath({
          shape,
          startX,
          startY,
          endX,
          endY,
          startSide,
          endSide,
        });

        const appearance = getEdgeAppearance(link.type);
        const label = getLinkLabel(link.raw, link.type);

        const labelCenter = getLabelCenter({ startX, startY, endX, endY });
        const { width: labelWidth, height: labelHeight } = getLabelDimensions(label);

        return (
          <g key={link.raw?.id ?? `${link.sourceId}-${link.targetId}-${idx}`}>
            <path
              d={path}
              className={appearance.className}
              fill="none"
              stroke={appearance.stroke}
              strokeWidth={2.5}
              markerEnd="url(#journey-map-arrowhead)"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {label ? (
              <g>
                <rect
                  x={labelCenter.x - labelWidth / 2}
                  y={labelCenter.y - labelHeight / 2}
                  rx={8}
                  ry={8}
                  width={labelWidth}
                  height={labelHeight}
                  fill="#ffffff"
                  stroke="var(--hbr-color-neutral-border-weak)"
                />
                <text
                  x={labelCenter.x}
                  y={labelCenter.y + 4}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="700"
                  fill="var(--hbr-color-neutral-text)"
                >
                  {label}
                </text>
              </g>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
};

export default JourneyMapEdgeLayer;